import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { getAllComponents } from '../data/aiOntology';
import { validateSolution, calculateSolutionCost } from '../utils/compatibilityEngine';
import { APPROACH_MATRIX } from '../data/approachMatrix';
import { SOLUTION_SIZING } from '../data/solutionSizing';
import { COMPONENT_GROUPINGS } from '../data/componentGroupings';

const SolutionContext = createContext();

const STORAGE_KEY = 'aiSolutionArchitectData';
const VERSION = '1.0.0';

const initialState = {
  // Solution Management
  solutions: [],
  activeSolutionId: null,
  selectedComponents: [],
  
  // Requirements & Constraints
  autoArchitectRequirements: null,
  approachConstraints: {},
  selectedApproaches: [],
  // Solution Meta Data
solutionMeta: {
  version: '1.0.0',
  author: '',
  stakeholders: [],
  solutionType: 'greenfield',
  deploymentType: 'standalone',
  environmentType: 'poc',
  reusability: 'single-use',
  audience: 'internal'
},

// Industry Details
industryDetails: {
  subIndustry: '',
  region: 'global',
  maturity: 'emerging'
},

// Scaling Details
scalingDetails: {
  expectedUsers: '',
  growthRate: '20',
  peakLoadMultiplier: '2',
  slaLevel: '99.9',
  p99Latency: '',
  capexVsOpex: 'opex',
  roiExpectation: '200',
  paybackPeriod: '12',
  multiCloud: false,
  cloudProviders: [],
  dataResidency: 'no-specific',
  auditLevel: 'basic',
  networkSecurity: 'standard',
  teamSize: '1-5',
  technicalSkills: [],
  trainingNeeds: [],
  timeToMarket: '3-months',
  innovationLevel: 'follower'
},

// Additional Inputs
additionalInputs: null,
  
  // Data
  transformationData: null,
  validationData: null,
  approachMatrixData: APPROACH_MATRIX,
  sizingData: SOLUTION_SIZING,
  groupingData: COMPONENT_GROUPINGS,
  
  // Component Specifications
  componentSpecifications: {},
  solutionSpecification: null,
  
  // Traceability
  traceabilityData: {
    forwardTrace: {},
    backwardTrace: {}
  },
  
  // UI State
  expandedNodes: new Set(['phase1', 'phase2', 'phase3', 'phase4', 'phase5', 'phase6', 'phase7']),
  searchQuery: '',
  validation: { issues: [], warnings: [], suggestions: [], isValid: true },
  totalCost: { low: 0, high: 0, currency: 'USD', breakdown: [] },
  uiState: {
    showComponentDetail: false,
    activeComponent: null,
    showValidationPanel: false,
    showExportModal: false,
    toast: null
  }
};

// ==================== LOAD FROM STORAGE ====================

const loadFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.expandedNodes) {
        parsed.expandedNodes = new Set(parsed.expandedNodes);
      }
      return {
        ...initialState,
        ...parsed,
        expandedNodes: parsed.expandedNodes || new Set(),
        solutions: parsed.solutions || [],
        sizingData: SOLUTION_SIZING,
        groupingData: COMPONENT_GROUPINGS,
        approachMatrixData: APPROACH_MATRIX
      };
    }
  } catch (error) {
    console.error('Error loading from localStorage:', error);
  }
  return initialState;
};

// ==================== SAVE TO STORAGE ====================

const saveToStorage = (state) => {
  try {
    const toSave = {
      ...state,
      expandedNodes: Array.from(state.expandedNodes || new Set()),
      lastSavedAt: new Date().toISOString(),
      version: VERSION,
      sizingData: undefined,
      groupingData: undefined,
      approachMatrixData: undefined
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

// ==================== GENERATE COMPONENT SPECIFICATIONS ====================

const generateComponentSpecifications = (selectedComponents, requirements, additionalInputs) => {
  const specification = SOLUTION_SIZING.generateSolutionSpecification(
    selectedComponents,
    requirements,
    additionalInputs
  );
  
  // Build component-specific specifications map
  const componentSpecs = {};
  specification.components.forEach(compSpec => {
    componentSpecs[compSpec.componentId] = compSpec;
  });
  
  return {
    componentSpecifications: componentSpecs,
    solutionSpecification: specification
  };
};

// ==================== REDUCER ====================

const solutionReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_COMPONENT': {
      const existingIndex = state.selectedComponents.findIndex(c => c.id === action.payload.id);
      let newComponents;
      
      if (existingIndex >= 0) {
        newComponents = [...state.selectedComponents];
        newComponents[existingIndex] = {
          ...newComponents[existingIndex],
          quantity: (newComponents[existingIndex].quantity || 1) + 1
        };
      } else {
        newComponents = [...state.selectedComponents, { ...action.payload, quantity: 1 }];
      }
      
      const allComponents = getAllComponents();
      
      // Generate specifications for new component set
      const specs = generateComponentSpecifications(
        newComponents,
        state.autoArchitectRequirements,
        state.additionalInputs
      );
      
      const updatedState = {
        ...state,
        selectedComponents: newComponents,
        validation: validateSolution(newComponents, allComponents),
        totalCost: calculateSolutionCost(newComponents),
        componentSpecifications: specs.componentSpecifications,
        solutionSpecification: specs.solutionSpecification
      };
      
      // Enrich components with specifications
      updatedState.selectedComponents = newComponents.map(comp => ({
        ...comp,
        specifications: specs.componentSpecifications[comp.id]?.specifications || null,
        sizingInfo: specs.componentSpecifications[comp.id] || null
      }));
      
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'REMOVE_COMPONENT': {
      const remaining = state.selectedComponents.filter(c => c.id !== action.payload.id);
      const allComponents = getAllComponents();
      const specs = generateComponentSpecifications(
        remaining,
        state.autoArchitectRequirements,
        state.additionalInputs
      );
      
      const updatedState = {
        ...state,
        selectedComponents: remaining.map(comp => ({
          ...comp,
          specifications: specs.componentSpecifications[comp.id]?.specifications || null,
          sizingInfo: specs.componentSpecifications[comp.id] || null
        })),
        validation: validateSolution(remaining, allComponents),
        totalCost: calculateSolutionCost(remaining),
        componentSpecifications: specs.componentSpecifications,
        solutionSpecification: specs.solutionSpecification
      };
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'UPDATE_QUANTITY': {
      const updated = state.selectedComponents
        .map(c => c.id === action.payload.id ? { ...c, quantity: Math.max(0, action.payload.quantity) } : c)
        .filter(c => c.quantity > 0);
      
      const allComponents = getAllComponents();
      const specs = generateComponentSpecifications(updated, state.autoArchitectRequirements, state.additionalInputs);
      
      const updatedState = {
        ...state,
        selectedComponents: updated.map(comp => ({
          ...comp,
          specifications: specs.componentSpecifications[comp.id]?.specifications || null,
          sizingInfo: specs.componentSpecifications[comp.id] || null
        })),
        validation: validateSolution(updated, allComponents),
        totalCost: calculateSolutionCost(updated),
        componentSpecifications: specs.componentSpecifications,
        solutionSpecification: specs.solutionSpecification
      };
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'CLEAR_SELECTION': {
      const updatedState = {
        ...state,
        selectedComponents: [],
        validation: { issues: [], warnings: [], suggestions: [], isValid: true },
        totalCost: { low: 0, high: 0, currency: 'USD', breakdown: [] },
        componentSpecifications: {},
        solutionSpecification: null
      };
      saveToStorage(updatedState);
      return updatedState;
    }
	
	case 'SET_CURRENT_VIEW': {
  return { 
    ...state, 
    uiState: { ...state.uiState, currentView: action.payload } 
  };
}
    
    case 'CREATE_SOLUTION': {
      const newSolution = {
        id: `sol-${Date.now()}`,
        name: action.payload.name || `Solution ${state.solutions.length + 1}`,
        description: action.payload.description || '',
        components: [...state.selectedComponents],
        autoArchitectRequirements: action.payload.requirements || state.autoArchitectRequirements,
        transformationData: action.payload.transformationData || null,
        validationData: action.payload.validationData || null,
        approachConstraints: action.payload.constraints || state.approachConstraints,
        selectedApproaches: action.payload.selectedApproaches || state.selectedApproaches,
        componentSpecifications: state.componentSpecifications,
        solutionSpecification: state.solutionSpecification,
        totalCost: state.totalCost,
        validation: state.validation,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const updatedState = {
        ...state,
        solutions: [...state.solutions, newSolution],
        activeSolutionId: newSolution.id
      };
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'LOAD_SOLUTION': {
      const solution = state.solutions.find(s => s.id === action.payload);
      if (!solution) return state;
      
      const updatedState = {
        ...state,
        activeSolutionId: solution.id,
        selectedComponents: solution.components || [],
        autoArchitectRequirements: solution.autoArchitectRequirements || null,
        transformationData: solution.transformationData || null,
        validationData: solution.validationData || null,
        approachConstraints: solution.approachConstraints || {},
        selectedApproaches: solution.selectedApproaches || [],
        componentSpecifications: solution.componentSpecifications || {},
        solutionSpecification: solution.solutionSpecification || null,
        totalCost: solution.totalCost || state.totalCost,
        validation: solution.validation || state.validation
      };
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'DELETE_SOLUTION': {
      const filtered = state.solutions.filter(s => s.id !== action.payload);
      const updatedState = {
        ...state,
        solutions: filtered,
        activeSolutionId: state.activeSolutionId === action.payload ? null : state.activeSolutionId
      };
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'SAVE_AUTO_ARCHITECT_REQUIREMENTS': {
      const updatedState = { ...state, autoArchitectRequirements: action.payload };
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'SAVE_ADDITIONAL_INPUTS': {
      const updatedState = { ...state, additionalInputs: action.payload };
      
      // Regenerate specifications with new inputs
      const specs = generateComponentSpecifications(
        state.selectedComponents,
        state.autoArchitectRequirements,
        action.payload
      );
      
      updatedState.componentSpecifications = specs.componentSpecifications;
      updatedState.solutionSpecification = specs.solutionSpecification;
      updatedState.selectedComponents = state.selectedComponents.map(comp => ({
        ...comp,
        specifications: specs.componentSpecifications[comp.id]?.specifications || null,
        sizingInfo: specs.componentSpecifications[comp.id] || null
      }));
      
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'SAVE_APPROACH_CONSTRAINTS': {
      const updatedState = { ...state, approachConstraints: action.payload };
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'SAVE_SELECTED_APPROACHES': {
      const updatedState = { ...state, selectedApproaches: action.payload };
      saveToStorage(updatedState);
      return updatedState;
    }
    
    case 'SAVE_TRACEABILITY_DATA': {
      const updatedState = {
        ...state,
        traceabilityData: {
          forwardTrace: action.payload.forwardTrace || {},
          backwardTrace: action.payload.backwardTrace || {}
        }
      };
      saveToStorage(updatedState);
      return updatedState;
    }
    
	case 'SAVE_SOLUTION_META': {
  const updatedState = { ...state, solutionMeta: action.payload };
  saveToStorage(updatedState);
  return updatedState;
}

case 'SAVE_INDUSTRY_DETAILS': {
  const updatedState = { ...state, industryDetails: action.payload };
  saveToStorage(updatedState);
  return updatedState;
}

case 'SAVE_SCALING_DETAILS': {
  const updatedState = { ...state, scalingDetails: action.payload };
  saveToStorage(updatedState);
  return updatedState;
}

case 'SAVE_ADDITIONAL_INPUTS': {
  const updatedState = { ...state, additionalInputs: action.payload };
  saveToStorage(updatedState);
  return updatedState;
}

case 'DUPLICATE_SOLUTION': {
  const sourceSolution = state.solutions.find(s => s.id === action.payload);
  if (!sourceSolution) return state;
  
  const newSolution = {
    ...sourceSolution,
    id: `sol-${Date.now()}`,
    name: `${sourceSolution.name} (Copy)`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  const updatedState = {
    ...state,
    solutions: [...state.solutions, newSolution],
    activeSolutionId: newSolution.id
  };
  saveToStorage(updatedState);
  return updatedState;
}

	
    case 'REFRESH_SPECIFICATIONS': {
      const specs = generateComponentSpecifications(
        state.selectedComponents,
        state.autoArchitectRequirements,
        state.additionalInputs
      );
      
      const updatedState = {
        ...state,
        componentSpecifications: specs.componentSpecifications,
        solutionSpecification: specs.solutionSpecification,
        selectedComponents: state.selectedComponents.map(comp => ({
          ...comp,
          specifications: specs.componentSpecifications[comp.id]?.specifications || null,
          sizingInfo: specs.componentSpecifications[comp.id] || null
        }))
      };
      return updatedState;
    }
    
    case 'INITIALIZE': {
      const loaded = loadFromStorage();
      return {
        ...loaded,
        expandedNodes: loaded.expandedNodes || new Set()
      };
    }
    
    case 'SHOW_TOAST': {
      return { ...state, uiState: { ...state.uiState, toast: action.payload } };
    }
    
    case 'CLEAR_TOAST': {
      return { ...state, uiState: { ...state.uiState, toast: null } };
    }
    
    case 'SHOW_COMPONENT_DETAIL': {
      // Enrich component with specifications if available
      const enrichedComponent = {
        ...action.payload,
        specifications: state.componentSpecifications[action.payload.id]?.specifications || null,
        sizingInfo: state.componentSpecifications[action.payload.id] || null
      };
      
      return {
        ...state,
        uiState: { ...state.uiState, showComponentDetail: true, activeComponent: enrichedComponent }
      };
    }
    
    case 'HIDE_COMPONENT_DETAIL': {
      return {
        ...state,
        uiState: { ...state.uiState, showComponentDetail: false, activeComponent: null }
      };
    }
    
    case 'TOGGLE_EXPAND': {
      const newExpanded = new Set(state.expandedNodes);
      if (newExpanded.has(action.payload)) newExpanded.delete(action.payload);
      else newExpanded.add(action.payload);
      return { ...state, expandedNodes: newExpanded };
    }
    
    default:
      return state;
  }
};

// ==================== PROVIDER ====================

export const SolutionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(solutionReducer, initialState);
  
  useEffect(() => {
    dispatch({ type: 'INITIALIZE' });
  }, []);
  
  useEffect(() => {
    if (state.solutions.length > 0 || state.selectedComponents.length > 0) {
      saveToStorage(state);
    }
  }, [state]);
  
  const saveSolution = useCallback((name, description, requirements, constraints, selectedApproaches) => {
    dispatch({ type: 'CREATE_SOLUTION', payload: { name, description, requirements, constraints, selectedApproaches } });
  }, []);
  
  const loadSolution = useCallback((id) => dispatch({ type: 'LOAD_SOLUTION', payload: id }), []);
  const deleteSolution = useCallback((id) => dispatch({ type: 'DELETE_SOLUTION', payload: id }), []);
  
  const saveAdditionalInputs = useCallback((inputs) => {
    dispatch({ type: 'SAVE_ADDITIONAL_INPUTS', payload: inputs });
  }, []);
  
  const saveApproachConstraints = useCallback((constraints) => {
    dispatch({ type: 'SAVE_APPROACH_CONSTRAINTS', payload: constraints });
  }, []);
  
  const refreshSpecifications = useCallback(() => {
    dispatch({ type: 'REFRESH_SPECIFICATIONS' });
  }, []);
  
  const getComponentSpecification = useCallback((componentId) => {
    return state.componentSpecifications[componentId] || null;
  }, [state.componentSpecifications]);
  
  const value = {
    state,
    dispatch,
    saveSolution,
    loadSolution,
    deleteSolution,
    saveAdditionalInputs,
    saveApproachConstraints,
    refreshSpecifications,
    getComponentSpecification
  };
  
  return (
    <SolutionContext.Provider value={value}>
      {children}
    </SolutionContext.Provider>
  );
};

export const useSolution = () => {
  const context = useContext(SolutionContext);
  if (!context) {
    throw new Error('useSolution must be used within SolutionProvider');
  }
  return context;
};

export default SolutionContext;